import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Archive, Play, ThumbsUp, ThumbsDown } from "lucide-react";

// Lot 780 — « Clôture automatique » : ticket résolu depuis ≥ N jours sans réaction du membre → clôturé + message doux.
// Réglage super admin (jours, marche/arrêt). Le membre garde son avis 👍/👎 et peut ouvrir un nouveau ticket.
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export const AutoCloseCard = ({ isSuper, onRan }) => {
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/tickets/autoclose-config").then(({ data }) => { setCfg(data); setDays(String(data.days)); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const save = async (patch) => {
    setBusy(true);
    try { const { data } = await api.patch("/admin/tickets/autoclose-config", patch); setCfg(data); setDays(String(data.days)); toast.success("Réglage enregistré"); }
    catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/tickets/autoclose-run"); toast.success(data.disabled ? "Clôture auto désactivée : rien fait" : `${data.closed} ticket(s) clôturé(s)`); load(); onRan?.(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const d = parseInt(days, 10);
  const lr = cfg.last_run;
  return (
    <div className="yalola-card p-4 space-y-2" data-testid="autoclose-card" data-enabled={cfg.enabled ? "1" : "0"}>
      <div className="flex items-center gap-2 flex-wrap">
        <Archive size={15} style={{ color: cfg.enabled ? "#0F766E" : "var(--yalola-text-muted)" }} />
        <b className="text-sm">Clôture automatique</b>
        <span className="chip text-[10px]" style={cfg.enabled ? { background: "#CCFBF1", color: "#115E59" } : {}} data-testid="autoclose-state">{cfg.enabled ? `Active · après ${cfg.days} j` : "Désactivée"}</span>
        {isSuper && <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ enabled: v })} disabled={busy} aria-label="Activer la clôture automatique" data-testid="autoclose-toggle" />}
        {isSuper && (
          <Button size="sm" variant="outline" className="rounded-full h-8 ml-auto" onClick={runNow} disabled={busy} data-testid="autoclose-run">
            <Play size={12} className="mr-1" /> Lancer maintenant
          </Button>
        )}
      </div>
      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="autoclose-help">
        Un ticket <b>résolu</b> sans réaction du membre pendant <b>{cfg.days} jour(s)</b> passe en « Clôturé » : un message doux est ajouté au fil (dans sa langue) et le membre est prévenu. Il peut toujours donner son avis 👍/👎 ou ouvrir un nouveau ticket ; l'équipe peut réouvrir.
      </p>
      {isSuper && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="autoclose-settings">
          <span>Clôturer après</span>
          <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className="w-20 h-8" data-testid="autoclose-days" />
          <span>jour(s)</span>
          <Button size="sm" className="pill-primary rounded-full h-8" disabled={busy || !(d >= 1 && d <= 365) || d === cfg.days} onClick={() => save({ days: d })} data-testid="autoclose-days-save">Enregistrer</Button>
        </div>
      )}
      {lr?.at && (
        <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="autoclose-last-run">
          Dernier passage : {fmt(lr.at)} — {lr.disabled ? "clôture désactivée" : `${lr.closed} clôturé(s) sur ${lr.checked} résolu(s) depuis plus de ${lr.days} j`}.
        </div>
      )}
    </div>
  );
};

// Lot 780 — avis du membre (👍 / 👎 + un mot) affiché à l'équipe dans la fiche et la liste.
export const FeedbackChip = ({ ticket, compact = false, testId = "ticket-feedback-chip" }) => {
  const fb = ticket?.feedback;
  if (!fb) return null;
  const up = fb.rating === "up";
  const style = up ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEF3C7", color: "#92400E" };
  return (
    <span className="chip text-[10px] inline-flex items-center gap-1 max-w-full" style={style} title={fb.comment ? `« ${fb.comment} »` : undefined} data-testid={testId} data-rating={fb.rating}>
      {up ? <ThumbsUp size={10} /> : <ThumbsDown size={10} />} {up ? "Utile" : "Pas aidé"}{!compact && fb.comment ? <span className="truncate font-normal"> · « {fb.comment} »</span> : null}
    </span>
  );
};
